/**
 * PenaltiesPage — Steward Admin
 * Award penalties to drivers, manage appeals, view audit log.
 */
import React, { useState, useEffect } from 'react';
import {
  onEvents, onDrivers, onResults, onPenalties,
  awardPenalty, updatePenaltyStatus, TITLES,
} from '../services/tripleCrown';
import useChampionship from '../hooks/useChampionship';

const REASONS = ['Intentional collision', 'Unsportsmanlike conduct', 'Cheating', 'Other'];

function AwardPenaltyModal({ events, drivers, onClose }) {
  const [eventId, setEventId] = useState('');
  const [results, setResults] = useState([]);
  const [resultId, setResultId] = useState('');
  const [points, setPoints] = useState(1);
  const [reason, setReason] = useState(REASONS[0]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Load results when event selected
  useEffect(() => {
    if (!eventId) { setResults([]); return; }
    return onResults(eventId, setResults);
  }, [eventId]);

  const handleSubmit = async () => {
    if (!resultId || !points || !reason) return;
    setSaving(true);
    try {
      await awardPenalty({ resultId, points: parseInt(points), reason, notes: notes.trim() });
      setToast({ type: 'success', msg: 'Penalty awarded' });
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Failed to award penalty' });
    } finally {
      setSaving(false);
    }
  };

  const completedEvents = events.filter(e => e.status === 'completed');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Award Penalty</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="form-group">
          <label className="form-label">Event</label>
          <select className="form-select" value={eventId} onChange={e => { setEventId(e.target.value); setResultId(''); }}>
            <option value="">Select event...</option>
            {completedEvents.map(evt => {
              const title = TITLES.find(t => t.id === evt.titleId);
              return (
                <option key={evt.id} value={evt.id}>
                  {title?.short} — {evt.phaseName || `Phase ${evt.phase}`}{evt.track ? ` — ${evt.track}` : ''}
                </option>
              );
            })}
          </select>
        </div>

        {results.length > 0 && (
          <div className="form-group">
            <label className="form-label">Driver (Result)</label>
            <select className="form-select" value={resultId} onChange={e => setResultId(e.target.value)}>
              <option value="">Select driver...</option>
              {results.map(r => {
                const driver = drivers.find(d => d.id === r.driver);
                return (
                  <option key={r.id} value={r.id}>
                    P{r.position} — {driver?.name || r.driver} ({r.finalPoints} pts)
                  </option>
                );
              })}
            </select>
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Penalty Points (1–5)</label>
            <input className="form-input" type="number" min="1" max="5" value={points} onChange={e => setPoints(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Reason</label>
            <select className="form-select" value={reason} onChange={e => setReason(e.target.value)}>
              {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Steward Notes</label>
          <textarea className="form-textarea" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Describe the incident in detail..." />
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={handleSubmit} disabled={saving || !resultId}>
            {saving ? 'Awarding...' : 'Award Penalty'}
          </button>
        </div>

        {toast && <div className={`toast toast-${toast.type}`} style={{ position: 'static', marginTop: 12 }}>{toast.msg}</div>}
      </div>
    </div>
  );
}

export default function PenaltiesPage() {
  const { champ } = useChampionship();
  const [events, setEvents] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [penalties, setPenalties] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!champ) { setEvents([]); setDrivers([]); setPenalties([]); return; }
    const unsubs = [
      onEvents(champ.id, setEvents),
      onDrivers(champ.id, setDrivers),
      onPenalties(champ.id, setPenalties),
    ];
    return () => unsubs.forEach(fn => fn());
  }, [champ]);

  const handleStatusChange = async (penaltyId, newStatus) => {
    try {
      await updatePenaltyStatus(penaltyId, newStatus);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Penalties & Review</h1>
          <p className="page-subtitle">Award and manage penalty points</p>
        </div>
        <button className="btn btn-danger" onClick={() => setModalOpen(true)}>+ Award Penalty</button>
      </div>

      {penalties.length === 0 ? (
        <div className="empty-state">
          <h3>No penalties issued</h3>
          <p>All drivers in good standing</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Driver</th>
                  <th>Points</th>
                  <th>Reason</th>
                  <th>Notes</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {penalties.map(pen => {
                  const driver = drivers.find(d => d.id === pen.driver);
                  const date = pen.createdAt?.toDate
                    ? pen.createdAt.toDate().toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'America/Barbados' })
                    : '—';
                  const statusBadge = pen.status === 'active' ? 'badge-pink'
                    : pen.status === 'appealed' ? 'badge-gold'
                    : pen.status === 'dismissed' ? 'badge-success'
                    : 'badge-muted';
                  return (
                    <tr key={pen.id}>
                      <td style={{ fontWeight: 600 }}>{driver?.name || pen.driver}</td>
                      <td style={{ color: 'var(--danger)', fontWeight: 700, fontSize: 16 }}>-{pen.points}</td>
                      <td>{pen.reason}</td>
                      <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {pen.notes || '—'}
                      </td>
                      <td><span className={`badge ${statusBadge}`}>{pen.status}</span></td>
                      <td>{date}</td>
                      <td style={{ textAlign: 'right' }}>
                        {pen.status === 'active' && (
                          <>
                            <button className="btn btn-secondary btn-sm" style={{ marginRight: 4 }}
                              onClick={() => handleStatusChange(pen.id, 'appealed')}>Appeal</button>
                          </>
                        )}
                        {pen.status === 'appealed' && (
                          <>
                            <button className="btn btn-secondary btn-sm" style={{ marginRight: 4 }}
                              onClick={() => handleStatusChange(pen.id, 'active')}>Uphold</button>
                            <button className="btn btn-secondary btn-sm"
                              onClick={() => handleStatusChange(pen.id, 'dismissed')}>Dismiss</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <AwardPenaltyModal
          events={events}
          drivers={drivers}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}
